import React, { useState, useRef, useEffect } from 'react';
import { ChatBubbleLeftRightIcon, XMarkIcon, PaperAirplaneIcon } from '@heroicons/react/24/outline';
import { API_BASE_URL } from '../../config/api';

const DakhlaBot = () => {
 const [isOpen, setIsOpen] = useState(false);
 const [messages, setMessages] = useState([
 {
 id: 1,
 text: "Hi! I'm DakhlaBot. Ask me about admissions, programs, deadlines, fees, requirements, recommendations, or career guidance.",
 sender: 'bot',
 timestamp: new Date(),
 sourcesUsed: [],
 followUpQuestion: null
 }
 ]);
 const [inputMessage, setInputMessage] = useState('');
 const [isTyping, setIsTyping] = useState(false);
 const messagesEndRef = useRef(null);
 const chatContainerRef = useRef(null);

 const scrollToBottom = () => {
 messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
 };

 useEffect(() => {
 scrollToBottom();
 }, [messages]);

 const quickReplies = [
 "Search Universities",
 "How to apply?",
 "Admission requirements",
 "Fee structure",
 "Career guidance"
 ];

 const sourceLabelMap = {
 recommendations: 'Recommendations',
 ml_model: 'ML Model',
 database: 'FAQ',
 groq: 'AI'
 };

 const handleSendMessage = async (message = inputMessage) => {
 if (!message.trim()) return;

 const userMessage = {
 id: Date.now(),
 text: message,
 sender: 'user',
 timestamp: new Date()
 };

 setMessages(prev => [...prev, userMessage]);
 setInputMessage('');
 setIsTyping(true);

 try {
 const token = localStorage.getItem('token');
 const response = await fetch(`${API_BASE_URL}/chatbot/message`, {
 method: 'POST',
 headers: {
 'Content-Type': 'application/json',
 ...(token ? { Authorization: `Bearer ${token}` } : {}),
 },
 body: JSON.stringify({ message }),
 });

 const data = await response.json();
 const botText = response.ok
 ? data.answer || 'I could not generate an answer right now.'
 : data.error || 'I could not process your request right now.';

 const botResponse = {
 id: Date.now() + 1,
 text: botText,
 sender: 'bot',
 timestamp: new Date(),
 sourcesUsed: Array.isArray(data.sources_used) ? data.sources_used : [],
 followUpQuestion: data.follow_up_question || null
 };
 setMessages(prev => [...prev, botResponse]);
 } catch (error) {
 const botResponse = {
 id: Date.now() + 1,
 text: 'Network error. Please try again in a moment.',
 sender: 'bot',
 timestamp: new Date(),
 sourcesUsed: [],
 followUpQuestion: null
 };
 setMessages(prev => [...prev, botResponse]);
 } finally {
 setIsTyping(false);
 }
 };

 const handleQuickReply = (reply) => {
 handleSendMessage(reply);
 };

 return (
 <>
 {/* Chat Toggle Button */}
 <button
 onClick={() => setIsOpen(!isOpen)}
 className={`fixed bottom-6 right-6 z-50 p-4 rounded-full shadow-lg transition-all duration-300 transform active:scale-95 hover:scale-105 flex items-center justify-center ${
 isOpen 
 ? 'bg-rose-500 hover:bg-rose-600 text-white' 
 : 'bg-gradient-to-tr from-primary-600 to-primary-700 text-white animate-pulse-ring'
 }`}
 style={{ width: '56px', height: '56px' }}
 >
 {isOpen ? (
 <XMarkIcon className="h-6 w-6" />
 ) : (
 <ChatBubbleLeftRightIcon className="h-6 w-6" />
 )}
 </button>

 {/* Chat Window */}
 {isOpen && (
 <div className="fixed bottom-24 right-6 z-50 w-[26rem] sm:w-[31rem] h-[36rem] bg-white rounded-2xl shadow-[0_20px_50px_rgba(99,102,241,0.15)] border border-slate-100 flex flex-col overflow-hidden transition-all duration-300 ease-out">
 {/* Chat Header */}
 <div className="bg-gradient-to-tr from-primary-600 to-primary-700 text-white p-5 flex items-center justify-between shadow-md">
 <div className="flex items-center">
 <div className="w-9 h-9 bg-white rounded-xl flex items-center justify-center mr-3 shadow-inner">
 <img
 src="/assets/logos/logo.svg"
 alt="DakhlaBot"
 className="h-6 w-6 block object-contain translate-x-[1px]"
 />
 </div>
 <div>
 <h3 className="font-bold text-base leading-snug">DakhlaBot</h3>
 <p className="text-[11px] text-primary-100/90 font-medium">Admission & Career Advisor</p>
 </div>
 </div>
 </div>

 {/* Messages Container */}
 <div 
 ref={chatContainerRef}
 className="flex-1 overflow-y-auto p-4 space-y-4 bg-slate-50"
 >
 {messages.map((message) => (
 <div
 key={message.id}
 className={`flex ${message.sender === 'user' ? 'justify-end' : 'justify-start'}`}
 >
 <div
 className={`max-w-xs sm:max-w-sm px-4 py-2.5 ${
 message.sender === 'user'
 ? 'bg-gradient-to-tr from-primary-600 to-primary-700 text-white rounded-2xl rounded-tr-none shadow-md shadow-primary-500/10'
 : 'bg-white text-slate-800 border border-slate-100 rounded-2xl rounded-tl-none shadow-sm'
 }`}
 >
 <p className="text-sm leading-relaxed">{message.text}</p>
 
 {message.sender === 'bot' && Array.isArray(message.sourcesUsed) && message.sourcesUsed.length > 0 && (
 <div className="mt-2.5 flex flex-wrap gap-1">
 {message.sourcesUsed.map((source) => (
 <span
 key={`${message.id}-${source}`}
 className="px-2.5 py-0.5 rounded-full text-[10px] font-semibold bg-indigo-50 border border-indigo-100/50 text-indigo-700 shadow-sm"
 >
 {sourceLabelMap[source] || source}
 </span>
 ))}
 </div>
 )}
 
 {message.sender === 'bot' && message.followUpQuestion && (
 <div className="mt-2.5 rounded-xl border border-primary-200/50 bg-primary-50/50 px-3.5 py-2.5 text-xs text-primary-800 shadow-sm leading-relaxed">
 {message.followUpQuestion}
 </div>
 )}
 
 <p className={`text-[10px] mt-1.5 font-medium ${
 message.sender === 'user' ? 'text-primary-100/80' : 'text-slate-400'
 }`}>
 {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
 </p>
 </div>
 </div>
 ))}

 {/* Typing Indicator */}
 {isTyping && (
 <div className="flex justify-start">
 <div className="bg-white text-slate-800 border border-slate-100 px-4 py-3 rounded-2xl rounded-tl-none shadow-sm">
 <div className="flex space-x-1.5 items-center">
 <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce"></div>
 <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '0.15s' }}></div>
 <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '0.3s' }}></div>
 </div>
 </div>
 </div>
 )}

 {/* Quick Replies */}
 {messages.length === 1 && (
 <div className="flex flex-wrap gap-2 mt-3 pl-1">
 {quickReplies.map((reply, index) => (
 <button
 key={index}
 onClick={() => handleQuickReply(reply)}
 className="px-3.5 py-1.5 bg-white border border-slate-200 text-slate-600 text-xs rounded-xl hover:bg-primary-50 hover:text-primary-700 hover:border-primary-200 shadow-sm active:scale-[0.97] transition-all duration-200 font-semibold cursor-pointer"
 >
 {reply}
 </button>
 ))}
 </div>
 )}

 <div ref={messagesEndRef} />
 </div>

 {/* Input Area */}
 <div className="p-4 border-t border-slate-100 bg-white">
 <div className="flex space-x-2">
 <input
 type="text"
 value={inputMessage}
 onChange={(e) => setInputMessage(e.target.value)}
 onKeyDown={(e) => e.key === 'Enter' && handleSendMessage()}
 placeholder="Type your message..."
 className="flex-1 border border-slate-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all text-slate-800 placeholder-slate-400"
 />
 <button
 onClick={() => handleSendMessage()}
 disabled={!inputMessage.trim()}
 className="bg-gradient-to-r from-primary-600 to-primary-700 hover:from-primary-700 hover:to-primary-800 disabled:from-slate-200 disabled:to-slate-200 disabled:text-slate-400 text-white p-3 rounded-xl transition-all shadow-md shadow-primary-500/10 disabled:shadow-none cursor-pointer"
 >
 <PaperAirplaneIcon className="h-4 w-4" />
 </button>
 </div>
 </div>
 </div>
 )}
 </>
 );
};

export default DakhlaBot;